import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import styled from "styled-components";
import {
  ActionIconContainer,
  AddFormButton,
  CustomLoader,
} from "../../../../shared/components/TableElements";
import { Trans, useTranslation } from "react-i18next";
import { colorTextAdditional } from "../../../../utils/palette";
import { post } from "../../../../api/getUser";
import { useToast } from "../../../../contexts/ToastContext";

const WEX_EMAIL = "cswexlink@wexinc.com";
const SF_EMAIL = "integrations@simply-fleet.com";

const WEX_FORM_URL =
  "https://na2.docusign.net/Member/PowerFormSigning.aspx?PowerFormId=05a6a6c4-d0ee-45ad-b58e-079c01efc2e4&env=na2&acct=abb114aa-2f2d-43bb-9e58-9065fb8fb749&v=2";

const Wex = ({ status, onClose, onFormSubmitted }) => {
  const { showToast } = useToast();
  const { t } = useTranslation("common");
  const isConnect = status === 1;
  const isSetupInProgress = status === 2;
  const isDisconnect = status === 3;

  const [saveBtnLoading, setBtnLoading] = useState(false);

  const modalTitle = isConnect
    ? t("connect_wex_fuel_card")
    : isSetupInProgress
      ? t("wex_setup_in_progress_title")
      : t("disconnect_wex_fuel_card");

  const formSubmitted = async () => {
    const apiData = { querystring: "wex_form_submitted" };
    try {
      setBtnLoading(true);
      const res = await post("integrations", apiData);
      if (res.success) {
        showToast("success", t("wex_request_received_msg"));
      } else {
        showToast("error", t("listing_err_msg"));
      }
    } catch (err) {
      showToast("error", t("listing_err_msg"));
    } finally {
      setBtnLoading(false);
      if (onFormSubmitted) {
        await onFormSubmitted();
      }
    }
  };

  return (
    <Modal
      show
      onHide={onClose}
      centered
      size="md"
      backdropClassName="custom-backdrop"
    >
      <Modal.Header>
        <h4>{modalTitle}</h4>
        <div
          className="d-flex justify-content-end"
          //style={{ width: "-webkit-fill-available" }}
        >
          <ActionIconContainer onClick={onClose}>
            <i className="fi fi-rr-cross-small" />
          </ActionIconContainer>
        </div>
      </Modal.Header>

      <Modal.Body style={{ paddingTop: "12px" }}>
        {isSetupInProgress ? (
          <>
            <BodyText>{t("wex_setup_in_progress_body")}</BodyText>
            <SubText>
              <Trans
                i18nKey="wex_setup_in_progress_subtext"
                t={t}
                components={[
                  <a
                    href={WEX_FORM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  />,
                ]}
              />
            </SubText>
            <FooterText>
              <Trans
                i18nKey="wex_connect_help"
                t={t}
                components={[<a href={`mailto:${SF_EMAIL}`} />]}
              />
            </FooterText>
          </>
        ) : isConnect ? (
          <>
            <BodyText>{t("wex_connect_intro")}</BodyText>
            <StepsList>
              <StepItem>
                <StepNumber>1</StepNumber>
                <StepText>
                  <Trans
                    i18nKey="wex_connect_step_1"
                    t={t}
                    components={[
                      <a
                        href={WEX_FORM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                      />,
                    ]}
                  />
                </StepText>
              </StepItem>
              <StepItem>
                <StepNumber>2</StepNumber>
                <StepText>
                  <Trans i18nKey="wex_connect_step_2" t={t} />
                </StepText>
              </StepItem>
              {/* <StepItem>
                <StepNumber>3</StepNumber>
                <StepText>
                  <Trans
                    i18nKey="wex_connect_step_3"
                    t={t}
                    components={[
                      <a href={`mailto:${WEX_EMAIL}`} />,
                      <a href={`mailto:${SF_EMAIL}`} />,
                    ]}
                  />
                </StepText>
              </StepItem>
              <StepItem>
                <StepNumber>4</StepNumber>
                <StepText>{t("wex_connect_step_4")}</StepText>
              </StepItem> */}
            </StepsList>
            <SubText>{t("wex_subtext")}</SubText>
            <FooterText>
              <Trans
                i18nKey="wex_connect_help"
                t={t}
                components={[<a href={`mailto:${SF_EMAIL}`} />]}
              />
            </FooterText>
          </>
        ) : (
          <>
            <BodyText>{t("wex_disconnect_intro")}</BodyText>
            <StepsList>
              <StepItem>
                <StepNumber>1</StepNumber>
                <StepText>
                  <Trans
                    i18nKey="wex_disconnect_step_1"
                    t={t}
                    components={[<a href={`mailto:${WEX_EMAIL}`} />]}
                  />
                </StepText>
              </StepItem>
              <StepItem>
                <StepNumber>2</StepNumber>
                <StepText>
                  <Trans
                    i18nKey="wex_disconnect_step_2"
                    t={t}
                    components={[<a href={`mailto:${SF_EMAIL}`} />]}
                  />
                </StepText>
              </StepItem>
            </StepsList>
            <FooterText>{t("wex_disconnect_footer")}</FooterText>
          </>
        )}
      </Modal.Body>

      {isConnect && (
        <Modal.Footer>
          <AddFormButton type="button" onClick={formSubmitted}>
            {saveBtnLoading ? <CustomLoader /> : t("ive_submitted_the_form")}
          </AddFormButton>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default Wex;

const BodyText = styled.p`
  font-size: 14px;
  color: #333;
  margin-bottom: 16px;
`;

const StepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
`;

const StepItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const StepNumber = styled.div`
  min-width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #1a1a2e;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StepText = styled.p`
  font-size: 14px;
  color: #333;
  margin: 0;
  padding-top: 4px;

  a {
    color: #0d6efd;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const SubText = styled.p`
  font-size: 13px;
  color: ${colorTextAdditional};
  margin-bottom: 12px;

  a {
    color: #0d6efd;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const FooterText = styled.p`
  font-size: 13px;
  color: #888;
  margin: 0;

  a {
    color: #0d6efd;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;
